import React, { useState } from 'react';
import {
  ActivityIndicator,
  Image,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Button, Input, Text, useTheme } from '@rneui/themed';
import PasswordInput from '../components/PasswordInput';
import OkDialog from '../components/OkDialog';
import { useAuth } from '../hooks/useAuth';

const SignInScreen = ({ navigation, reloadUser }) => {
  const { theme } = useTheme();
  const { user, sendEmailVerification, signInWithEmailAndPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState();
  const [sentDialogVisible, setSentDialogVisible] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);

  const onSignIn = async () => {
    try {
      //console.log('--- Loggin in, email, password');
      const userCredential = await signInWithEmailAndPassword(email, password);
    } catch (error) {
      console.error(error);
      setErrorDialogVisible(true);
    }
  };

  const onCheckEmailVerification = async () => {
    try {
      setSending(true);
      await reloadUser();
    } catch (error) {
      console.error(error);
      setErrorDialogVisible(true);
    } finally {
      setSending(false);
    }
  };

  const onResendEmailVerification = async () => {
    try {
      setSending(true);
      await sendEmailVerification();
      setSentDialogVisible(true);
    } catch (error) {
      console.error(error);
      setErrorDialogVisible(true);
    } finally {
      setSending(false);
    }
  };

  const renderCheckVerification = () => {
    return (
      <View style={{ rowGap: 10 }}>
        <OkDialog
          title="Återställningslänk skickad!"
          body="Kolla din e-post och använd länken för att återställa ditt lösenord."
          visible={sentDialogVisible}
          onOkPress={() => {
            setSentDialogVisible(false);
            navigation.goBack();
          }}
        />
        <OkDialog
          title="Kunde inte utföra"
          body={`Kunde utföra uppgiften för ${user.email}. Kontrollera din uppkoppling och försök igen lite senare.`}
          visible={errorDialogVisible}
          onOkPress={() => setErrorDialogVisible(false)}
        />
        <Button
          title="Jag har verifierat länken"
          buttonStyle={{ backgroundColor: 'white' }}
          titleStyle={{ color: theme.colors.primary }}
          onPress={onCheckEmailVerification}
          disabled={sending}
          disabledStyle={{
            backgroundColor: 'lightgrey',
          }}
          disabledTitleStyle={{ color: theme.colors.primaryTrans }}
        />
        <Button
          buttonStyle={{ borderColor: 'white' }}
          title="Skicka länken igen"
          onPress={onResendEmailVerification}
          disabled={sending}
          disabledStyle={{
            borderColor: 'lightgrey',
            borderWidth: 1,
          }}
          disabledTitleStyle={{ color: 'lightgrey' }}
          loading={sending}
        />
      </View>
    );
  };

  const renderSignin = () => {
    if (user === undefined) {
      return <ActivityIndicator size="large" color="white" />;
    }

    return (
      <View style={{ rowGap: 10 }}>
        <OkDialog
          title="Kunde inte logga in"
          body={`Kunde inte logga in ${email}. Kontrollera användarnamnet och lösenordet.`}
          visible={errorDialogVisible}
          onOkPress={() => setErrorDialogVisible(false)}
        />
        <Text style={{ color: 'white' }}></Text>
        <Input
          style={styles.input}
          inputContainerStyle={{ borderBottomColor: 'white' }}
          keyboardType="email-address"
          placeholder="E-post"
          placeholderTextColor="lightgrey"
          onChangeText={(text) => setEmail(text)}
          value={email}
        />
        <Text></Text>
        <PasswordInput
          style={styles.input}
          inputContainerStyle={{ borderBottomColor: 'white' }}
          placeholder="Lösenord"
          onChangeText={(text) => setPassword(text)}
          value={password}
        />
        <Button
          title="Logga in"
          onPress={onSignIn}
          style={{ marginTop: 25 }}
          buttonStyle={{ backgroundColor: 'white' }}
          titleStyle={{ color: theme.colors.primary }}
        />
        <Button
          title="Glömt lösenordet"
          onPress={() => navigation.navigate('ResetPasswordScreen')}
          type="outline"
          buttonStyle={{ borderColor: 'white' }}
          titleStyle={{ color: 'white' }}
        />
        <Button
          title="Har ingen användare"
          onPress={() => navigation.navigate('SignUpScreen')}
          type="outline"
          buttonStyle={{ borderColor: 'white' }}
          titleStyle={{ color: 'white' }}
        />
      </View>
    );
  };

  return (
    <ScrollView
      contentContainerStyle={[
        styles.contentContainer,
        { backgroundColor: theme.colors.primary },
      ]}
    >
      <View style={styles.container}>
        <View style={{ rowGap: 25, marginBottom: 50 }}>
          <View style={styles.headerContainer}>
            <Text h1 style={styles.text}>
              Glasklart
            </Text>
          </View>
          <View style={styles.logoContainer}>
            <Image
              style={styles.logoImage}
              source={require('../../assets/icon.png')}
            />
          </View>
        </View>
        <View style={{ rowGap: 35 }}>
          {user ? renderCheckVerification() : renderSignin()}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 30,
  },
  container: {
    flex: 1,
    alignItems: 'center',
  },
  headerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 20,
  },
  logoContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoImage: {
    width: 192,
    height: 192,
  },
  input: {
    color: 'white',
  },
  buttonContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    rowGap: 25,
  },
  button: {
    marginBottom: 20,
  },
  text: {
    textAlign: 'center',
    color: 'white',
  },
});

export default SignInScreen;
